import { useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthContext'
import { clearStagedAssertion, getStagedAssertion } from './callbackAssertion'
import { WORDPRESS_LOGIN_URL } from './config'

type CallbackFailure = {
  title: string
  description: string
}

type AuthCallbackPageProps = {
  onContinue: () => void
}

function errorStatus(error: unknown) {
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

function friendlyFailure(error: unknown): CallbackFailure {
  switch (errorStatus(error)) {
    case 0:
      return {
        title: '로그인 서버에 연결하지 못했어요',
        description: '네트워크 연결을 확인한 뒤 다시 로그인해 주세요. 비회원으로도 계속 이용할 수 있습니다.',
      }
    case 401:
      return {
        title: '로그인 정보를 확인하지 못했어요',
        description: '로그인 시간이 지났거나 이미 사용된 정보입니다. 다시 로그인해 주세요.',
      }
    case 403:
      return {
        title: '이 계정으로 로그인을 완료할 수 없어요',
        description: '계정의 핑퐁랜드 로그인 상태를 확인한 뒤 다시 시도해 주세요. 비회원으로도 계속 이용할 수 있습니다.',
      }
    case 503:
      return {
        title: '로그인 서버가 잠시 응답하지 않아요',
        description: '잠시 후 다시 로그인해 주세요. 지금은 비회원으로 모든 기능을 계속 이용할 수 있습니다.',
      }
    default:
      return {
        title: error instanceof TypeError
          ? '로그인 서버에 연결하지 못했어요'
          : '로그인을 완료하지 못했어요',
        description: '네트워크 연결을 확인한 뒤 다시 로그인해 주세요. 비회원으로도 계속 이용할 수 있습니다.',
      }
  }
}

export function AuthCallbackPage({ onContinue }: AuthCallbackPageProps) {
  const {
    completeSso,
    logout,
  } = useAuth()
  const assertionRef = useRef(getStagedAssertion())
  const exchangeStartedRef = useRef(false)
  const mountedRef = useRef(false)
  const [failure, setFailure] = useState<CallbackFailure | null>(null)

  useEffect(() => {
    mountedRef.current = true

    if (!exchangeStartedRef.current) {
      exchangeStartedRef.current = true
      const assertion = assertionRef.current

      if (!assertion) {
        clearStagedAssertion()
        logout()
        onContinue()
      } else {
        void (async () => {
          try {
            await completeSso(assertion)
            clearStagedAssertion(assertion)
            if (mountedRef.current) onContinue()
          } catch (error) {
            clearStagedAssertion(assertion)
            if (mountedRef.current) setFailure(friendlyFailure(error))
          }
        })()
      }
    }

    return () => {
      mountedRef.current = false
    }
  }, [completeSso, logout, onContinue])

  const continueAsGuest = () => {
    try {
      logout()
    } catch {
      // Continuing as a guest must remain available even if local cleanup is
      // already complete or browser storage is restricted.
    }
    onContinue()
  }

  return (
    <main className="auth-callback-page">
      <section
        className={`auth-callback-card ${failure ? 'auth-callback-card-error' : 'auth-callback-card-working'}`}
        aria-live="polite"
        aria-busy={!failure}
      >
        {failure ? (
          <>
            <span className="auth-callback-symbol" aria-hidden="true">!</span>
            <h1>{failure.title}</h1>
            <p>{failure.description}</p>
            <div className="auth-callback-actions">
              <a href={WORDPRESS_LOGIN_URL}>다시 로그인</a>
              <button type="button" onClick={continueAsGuest}>비회원으로 계속</button>
            </div>
          </>
        ) : (
          <>
            <span className="auth-callback-spinner" aria-hidden="true" />
            <h1>핑퐁랜드에 로그인하고 있어요</h1>
            <p>잠시만 기다려 주세요.</p>
          </>
        )}
      </section>
    </main>
  )
}
